import './EmployeeLoginForm.scss'
import { ReactComponent as EmailIcon } from "../../assets/EmailIcon.svg";
import { ReactComponent as JCRLogo } from "../../../../../assets/logo/JCR_LOGO_BLACK.svg";
import { ReactComponent as KeyIcon } from "../../assets/KeyIcon.svg";
import { useState } from 'react';
import { Controller, useForm } from "react-hook-form"
import { joiResolver } from "@hookform/resolvers/joi";
import { classNames } from "primereact/utils"
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import CountDown from 'react-countdown';
import PrimeInputText from '../../../../../components/prime-input-text/PrimeInputText';
import PrimaryButton from '../../../../../components/button/PrimaryButton';
import PrimeInputPassword from '../../../../../components/prime-input-password/PrimeInputPassword';
import { setUserCredentials } from './../../../LoginAuthSlice';
import { useLoginJCRUserMutation, useVerifyJCRUserOTPMutation } from '../../../../../services/userAuth';
import { FORM_FIELD_KEYS, operationsEmailSchema, operationsOtpSchema } from "./EmployeeLoginConfig"
import { HOME_ROUTE } from '../../../../../config/routes';


function EnterEmail({ onEmailEntered, isLoading }) {

  const { control, formState: { errors }, handleSubmit } = useForm({
    resolver: joiResolver(operationsEmailSchema),
    defaultValues: {
      [FORM_FIELD_KEYS.EMAIL]: ''
    }
  });

  return (
    <form onSubmit={handleSubmit(onEmailEntered)} className='p-fluid' >
      <div className="field">
        <label htmlFor={FORM_FIELD_KEYS.EMAIL} className={classNames({ 'p-error': errors[FORM_FIELD_KEYS.EMAIL] }, 'font-semibold text-md mb-4')}>Enter your email</label>
        <p className='FontFamilyOpenSans text-sm font-semibold mb-5'>Please enter your email address for verification and we will proceed to log you in.</p>
        <Controller name={FORM_FIELD_KEYS.EMAIL} control={control} render={({ field, fieldState }) => (

          <PrimeInputText
            id={field[FORM_FIELD_KEYS.EMAIL]} {...field} className={classNames({ 'p-invalid': fieldState.invalid })}
            leftIcon={<EmailIcon />} isTypeInset={true} placeholder="john.smith@example.com"
          />

        )} />
        {errors[FORM_FIELD_KEYS.EMAIL] && <small className='p-error font-medium' >{errors[FORM_FIELD_KEYS.EMAIL].message}</small>}
      </div>

      <div className="flex justify-content-center align-items-center">
        <PrimaryButton type='submit' loading={isLoading} className='my-6 font-semibold py-2'>Send OTP</PrimaryButton>
      </div>

    </form>
  )
}


function EnterOtp({ onOTPEntered, isVerifying, isSendingMail, onResend }) {
  const { control, formState: { errors }, handleSubmit } = useForm({
    resolver: joiResolver(operationsOtpSchema),
    defaultValues: {
      [FORM_FIELD_KEYS.OTP]: ''
    }
  });

  const handleCountdown = ({ completed, seconds, minutes, api }) => {

    if (completed) {
      return <Button type='button' label="Resend OTP" onClick={(e) => { onResend(e); api.start(); }} loading={isSendingMail} className="mb-3 text-sm font-semibold ml-auto w-auto p-button-danger p-button-text" />
    }

    return <span className='text-sm mb-5 ml-auto text-danger text-right'>Resend OTP in <span className='font-semibold'>{minutes}:{seconds}s</span></span>
  }
  return (
    <form onSubmit={handleSubmit(onOTPEntered)} className='p-fluid' >

      <div className="field">
        <label htmlFor={FORM_FIELD_KEYS.OTP} className={classNames({ 'p-error': errors[FORM_FIELD_KEYS.OTP] }, 'font-semibold text-md mb-4')}>Enter OTP</label>
        <p className='FontFamilyOpenSans text-sm font-semibold mb-5'>Protecting your account is our top priority. Please confirm your account by entering the verification code send to your email.</p>
        <Controller name={FORM_FIELD_KEYS.OTP} control={control} render={({ field, fieldState }) => (

          <PrimeInputPassword
            id={field[FORM_FIELD_KEYS.OTP]} {...field} className={classNames({ 'p-invalid': fieldState.invalid })}
            leftIcon={<KeyIcon />} feedback={false} isTypeInset={true} placeholder="••••••••••"
          />

        )} />
        {errors[FORM_FIELD_KEYS.OTP] && <small className='p-error font-medium' >{errors[FORM_FIELD_KEYS.OTP].message}</small>}
      </div>

      <div className="flex flex-column justify-content-center align-items-center">
        <PrimaryButton type='submit' loading={isVerifying} className='my-4 font-semibold py-2'>Login</PrimaryButton>
        <CountDown date={Date.now() + (60000 * 2)} renderer={handleCountdown} />
      </div>
    </form>
  )
}

// Main Component
function EmployeeLoginForm() {
  const [otpSent, setOtpSent] = useState(false)
  const [email, setEmail] = useState(null)
  const [loginJCRUser, { isLoading }] = useLoginJCRUserMutation()
  const [verifyOtpJCRUser, { isLoading: isVerifying }] = useVerifyJCRUserOTPMutation()
  const { state } = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const handleEmailEntered = async ({ email }) => {

    const response = await loginJCRUser({ email }).unwrap()

    if (response.emailSent) {
      setOtpSent(true)
      toast.success('OTP sent on your E-Mail', { icon: '👍' })
      setEmail(email)
    }

  }
  const handleOTPEntered = async ({ otp }) => {
    const loginResponse = await verifyOtpJCRUser({ otp, email }).unwrap()
    if (loginResponse?.user) {
      const { user } = loginResponse
      dispatch(setUserCredentials({ user }))

      const pathname = state?.from?.pathname ?? HOME_ROUTE

      navigate(pathname)
    }
  }

  let content = !otpSent ? <EnterEmail onEmailEntered={handleEmailEntered} isLoading={isLoading} />
    : <EnterOtp onOTPEntered={handleOTPEntered} isSendingMail={isLoading} isVerifying={isVerifying} onResend={() => handleEmailEntered({ email })} />

  return (
    <section className="EmployeeLoginForm FontFamilyPoppins h-full flex justify-content-center align-items-center">
      <div className='bg-white shadow-4 w-full md:w-10 px-4 md:px-7'>
        <div className="flex justify-content-center align-items-center pb-5 pt-7 mb-3">
          <JCRLogo style={{ width: '13rem' }} className="mx-auto EmployeeLoginFormLogo" />
        </div>
        {content}
      </div>
    </section>
  )
}

export default EmployeeLoginForm